import type { Account, AppData, LedgerSummary, Transfer, TransferEvent, TransferState } from "./types";

export const activeEvents = (events: TransferEvent[]) => {
  const undone = new Set(events.filter((e) => e.kind === "undo" && e.targetEventId).map((e) => e.targetEventId!));
  return events.filter((e) => e.kind !== "undo" && !undone.has(e.id));
};
export const transferPath = (t: Transfer) => [t.sourceAccountId, ...t.routeAccountIds, t.destinationAccountId];

export function deriveTransferState(t: Transfer): Pick<Transfer, "state" | "currentLegIndex" | "completedAt"> {
  const events = activeEvents(t.events);
  if (events.some((e) => e.kind === "cancel")) return { state: "cancelled", currentLegIndex: 0, completedAt: undefined };
  const legs = transferPath(t).length - 1;
  const departed = new Set(events.filter((e) => e.kind === "depart").map((e) => e.legIndex));
  const arrived = new Set(events.filter((e) => e.kind === "arrive").map((e) => e.legIndex));
  if (arrived.has(legs - 1)) return { state: "completed", currentLegIndex: legs - 1, completedAt: [...events].reverse().find((e) => e.kind === "arrive" && e.legIndex === legs - 1)?.at };
  for (let i = 0; i < legs; i += 1) {
    if (departed.has(i) && !arrived.has(i)) return { state: "in_transit", currentLegIndex: i, completedAt: undefined };
    if (arrived.has(i) && i < legs - 1 && !departed.has(i + 1)) return { state: "at_intermediate", currentLegIndex: i + 1, completedAt: undefined };
  }
  return { state: "planned", currentLegIndex: 0, completedAt: undefined };
}

export function calculateLedger(data: Pick<AppData, "accounts" | "transfers" | "adjustments">): LedgerSummary {
  const balances: Record<string, number> = Object.fromEntries(data.accounts.map((a) => [a.id, a.openingBalanceYen]));
  let inTransitTotal = 0;
  for (const t of data.transfers) {
    const path = transferPath(t);
    for (const e of activeEvents(t.events)) {
      if (e.kind === "depart") { balances[path[e.legIndex]] = (balances[path[e.legIndex]] ?? 0) - t.amountYen; inTransitTotal += t.amountYen; }
      if (e.kind === "arrive") { balances[path[e.legIndex + 1]] = (balances[path[e.legIndex + 1]] ?? 0) + t.amountYen; inTransitTotal -= t.amountYen; }
    }
  }
  for (const a of data.adjustments) balances[a.accountId] = (balances[a.accountId] ?? 0) + a.deltaYen;
  const bank = new Set(data.accounts.filter((a) => a.type === "bank").map((a) => a.id));
  const brokerage = new Set(data.accounts.filter((a) => a.type === "brokerage").map((a) => a.id));
  const bankTotal = Object.entries(balances).reduce((s, [id, v]) => s + (bank.has(id) ? v : 0), 0);
  const brokerageTotal = Object.entries(balances).reduce((s, [id, v]) => s + (brokerage.has(id) ? v : 0), 0);
  return { balances, bankTotal, brokerageTotal, inTransitTotal, managedTotal: bankTotal + brokerageTotal + inTransitTotal, unfinishedCount: data.transfers.filter((t) => !["completed", "cancelled"].includes(deriveTransferState(t).state)).length };
}

export function nextTransferAction(t: Transfer) {
  const d = deriveTransferState(t); const path = transferPath(t);
  if (["completed", "cancelled"].includes(d.state)) return null;
  if (d.state === "in_transit") return { kind: "arrive" as const, legIndex: d.currentLegIndex, label: d.currentLegIndex === path.length - 2 ? "最終着金を確認・完了" : "銀行への着金を確認" };
  return { kind: "depart" as const, legIndex: d.currentLegIndex, label: d.currentLegIndex === 0 ? "出金済みにする" : "銀行から出金済みにする" };
}

export function accountLocation(t: Transfer, accounts: Account[]) {
  const names = Object.fromEntries(accounts.map((a) => [a.id, a.name])); const d = deriveTransferState(t); const path = transferPath(t);
  if (d.state === "in_transit") return `${names[path[d.currentLegIndex]] ?? "出金元"}から移動中`;
  if (d.state === "at_intermediate") return `${names[path[d.currentLegIndex]] ?? "経由口座"}に着金済み`;
  if (d.state === "completed") return `${names[path.at(-1)!] ?? "入金先"}に着金済み`;
  if (d.state === "cancelled") return "取消済み";
  return `${names[path[0]] ?? "出金元"}に所在`;
}

export function makeTransfer(p: { sourceAccountId: string; destinationAccountId: string; routeAccountIds?: string[]; amountYen: number; note?: string }): Transfer {
  const now = new Date().toISOString(); const id = crypto.randomUUID();
  return { id, sourceAccountId: p.sourceAccountId, destinationAccountId: p.destinationAccountId, routeAccountIds: p.routeAccountIds ?? [], amountYen: p.amountYen, currentLegIndex: 0, state: "planned", createdAt: now, updatedAt: now, note: p.note?.trim() || undefined, events: [{ id: crypto.randomUUID(), transferId: id, operationId: crypto.randomUUID(), kind: "create", at: now, legIndex: 0 }] };
}

export function appendTransferEvent(t: Transfer, kind: "depart" | "arrive" | "cancel" | "undo", legIndex: number, targetEventId?: string): Transfer {
  const now = new Date().toISOString();
  const updated: Transfer = { ...t, updatedAt: now, events: [...t.events, { id: crypto.randomUUID(), transferId: t.id, operationId: crypto.randomUUID(), kind, at: now, legIndex, targetEventId }] };
  return { ...updated, ...deriveTransferState(updated) };
}
export const displayStatus = (s: TransferState) => s === "completed" ? "完了" : s === "at_intermediate" ? "移動中・銀行着金済み" : s === "in_transit" ? "移動中" : s === "cancelled" ? "取消済み" : "移動予定";
export const yen = (value: number) => `${new Intl.NumberFormat("ja-JP").format(value)}円`;
